import type { Folder } from '@mystiq/contracts';
import { useState } from 'react';
import { useFolders } from '../api/hooks.ts';
import { ErrorLine, Modal } from './ui.tsx';

/** Folder paths like "Research / Papers" for a flat folder list. */
export function folderPaths(folders: Folder[]): Array<{ id: string; path: string; isInbox: boolean }> {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const pathOf = (folder: Folder): string => {
    const names = [folder.name];
    let parent = folder.parentId ? byId.get(folder.parentId) : undefined;
    for (let depth = 0; parent && depth < 50; depth++) {
      names.unshift(parent.name);
      parent = parent.parentId ? byId.get(parent.parentId) : undefined;
    }
    return names.join(' / ');
  };
  return folders
    .map((folder) => ({ id: folder.id, path: pathOf(folder), isInbox: folder.systemRole === 'inbox' }))
    .sort((a, b) => (a.isInbox ? -1 : b.isInbox ? 1 : a.path.localeCompare(b.path)));
}

export function MoveDialog({
  ws,
  currentFolderId,
  onMove,
  onClose,
}: {
  ws: string;
  currentFolderId: string;
  onMove(folderId: string): Promise<unknown>;
  onClose(): void;
}) {
  const folders = useFolders(ws);
  const [target, setTarget] = useState(currentFolderId);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Move to folder" onClose={onClose}>
      <label className="field">
        <span className="field-label">Folder</span>
        <select className="select" value={target} onChange={(event) => setTarget(event.target.value)}>
          {folderPaths(folders.data ?? []).map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.path}
            </option>
          ))}
        </select>
      </label>
      <ErrorLine error={error} />
      <div className="modal-actions">
        <button
          className="button button-primary"
          disabled={busy || target === currentFolderId}
          onClick={async () => {
            setBusy(true);
            try {
              await onMove(target);
              onClose();
            } catch (caught) {
              setError(caught);
              setBusy(false);
            }
          }}
        >
          Move here
        </button>
        <button className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
